/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: QueueProfileTierRulesSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Queue Profiles module.
----------------------------------------------------------------*/

import { Settings } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { QueueProfileFormState } from '../queueProfiles/types';

interface QueueProfileTierRulesSectionProps {
  formData: QueueProfileFormState;
  onFieldChange: <K extends keyof QueueProfileFormState>(field: K, value: QueueProfileFormState[K]) => void;
}

export function QueueProfileTierRulesSection({
  formData,
  onFieldChange
}: QueueProfileTierRulesSectionProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('queueProfiles.form.sections.tierRules')} icon={<Settings className="h-5 w-5" />}>
      <div className="grid grid-cols-12 gap-3">
        {/* Tier Rules Apply */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.tierRulesApply.tooltip')}>
            <Label htmlFor="tierRulesApply" className="text-left">
              {t('queueProfiles.form.fields.tierRulesApply.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="tierRulesApply"
            value={formData.tierRulesApply}
            onChange={(value) => onFieldChange('tierRulesApply', value)}
          />
        </div>

        {/* Tier Rule Wait */}
        <SimpleTooltip content={t('queueProfiles.form.fields.tierRuleWait.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="tierRuleWait" className="text-left">
            {t('queueProfiles.form.fields.tierRuleWait.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="tierRuleWait"
            type="number"
            min="0"
            value={formData.tierRuleWait}
            onChange={(event) => onFieldChange('tierRuleWait', event.target.value)}
            placeholder={t('queueProfiles.form.fields.tierRuleWait.placeholder')}
          />
        </div>

        {/* Tier Rule Multiply Level */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.tierRuleMultiplyLevel.tooltip')}>
            <Label htmlFor="tierRuleMultiplyLevel" className="text-left">
              {t('queueProfiles.form.fields.tierRuleMultiplyLevel.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="tierRuleMultiplyLevel"
            value={formData.tierRuleMultiplyLevel}
            onChange={(value) => onFieldChange('tierRuleMultiplyLevel', value)}
          />
        </div>

        {/* Tier Rule No Agent No Wait */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.tierRuleNoAgentNoWait.tooltip')}>
            <Label htmlFor="tierRuleNoAgentNoWait" className="text-left">
              {t('queueProfiles.form.fields.tierRuleNoAgentNoWait.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="tierRuleNoAgentNoWait"
            value={formData.tierRuleNoAgentNoWait}
            onChange={(value) => onFieldChange('tierRuleNoAgentNoWait', value)}
          />
        </div>

        {/* Tier Rule Max Wait */}
        <SimpleTooltip content={t('queueProfiles.form.fields.tierRuleMaxWait.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="tierRuleMaxWait" className="text-left">
            {t('queueProfiles.form.fields.tierRuleMaxWait.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="tierRuleMaxWait"
            type="number"
            min="0"
            value={formData.tierRuleMaxWait}
            onChange={(event) => onFieldChange('tierRuleMaxWait', event.target.value)}
            placeholder={t('queueProfiles.form.fields.tierRuleMaxWait.placeholder')}
          />
        </div>

        {/* Tier Wait Stop Sound */}
        <SimpleTooltip content={t('queueProfiles.form.fields.tierWaitStopSound.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="tierWaitStopSound" className="text-left">
            {t('queueProfiles.form.fields.tierWaitStopSound.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="tierWaitStopSound"
            value={formData.tierWaitStopSound}
            onChange={(event) => onFieldChange('tierWaitStopSound', event.target.value)}
            placeholder={t('queueProfiles.form.fields.tierWaitStopSound.placeholder')}
          />
        </div>

        {/* Tier Rule Level Stop Sound */}
        <SimpleTooltip content={t('queueProfiles.form.fields.tierRuleLevelStopSound.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="tierRuleLevelStopSound" className="text-left">
            {t('queueProfiles.form.fields.tierRuleLevelStopSound.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="tierRuleLevelStopSound"
            value={formData.tierRuleLevelStopSound}
            onChange={(event) => onFieldChange('tierRuleLevelStopSound', event.target.value)}
            placeholder={t('queueProfiles.form.fields.tierRuleLevelStopSound.placeholder')}
          />
        </div>

        {/* Tier Skip Busy Agents */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.tierSkipBusyAgents.tooltip')}>
            <Label htmlFor="tierSkipBusyAgents" className="text-left">
              {t('queueProfiles.form.fields.tierSkipBusyAgents.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="tierSkipBusyAgents"
            value={formData.tierSkipBusyAgents}
            onChange={(value) => onFieldChange('tierSkipBusyAgents', value)}
          />
        </div>

        {/* Tier Randomize Agents */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.tierRandomizeAgents.tooltip')}>
            <Label htmlFor="tierRandomizeAgents" className="text-left">
              {t('queueProfiles.form.fields.tierRandomizeAgents.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="tierRandomizeAgents"
            value={formData.tierRandomizeAgents}
            onChange={(value) => onFieldChange('tierRandomizeAgents', value)}
          />
        </div>

        {/* Reserve Agents */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.reserveAgents.tooltip')}>
            <Label htmlFor="reserveAgents" className="text-left">
              {t('queueProfiles.form.fields.reserveAgents.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="reserveAgents"
            value={formData.reserveAgents}
            onChange={(value) => onFieldChange('reserveAgents', value)}
          />
        </div>

        {/* Truncate Tiers on Load */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.truncateTiersOnLoad.tooltip')}>
            <Label htmlFor="truncateTiersOnLoad" className="text-left">
              {t('queueProfiles.form.fields.truncateTiersOnLoad.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="truncateTiersOnLoad"
            value={formData.truncateTiersOnLoad}
            onChange={(value) => onFieldChange('truncateTiersOnLoad', value)}
          />
        </div>

        {/* Tier No Wait */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.tierNoWait.tooltip')}>
            <Label htmlFor="tierNoWait" className="text-left">
              {t('queueProfiles.form.fields.tierNoWait.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="tierNoWait"
            value={formData.tierNoWait}
            onChange={(value) => onFieldChange('tierNoWait', value)}
          />
        </div>

        {/* Tier Agent No Wait */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queueProfiles.form.fields.tierAgentNoWait.tooltip')}>
            <Label htmlFor="tierAgentNoWait" className="text-left">
              {t('queueProfiles.form.fields.tierAgentNoWait.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="tierAgentNoWait"
            value={formData.tierAgentNoWait}
            onChange={(value) => onFieldChange('tierAgentNoWait', value)}
          />
        </div>

        {/* Tier Retry Wait */}
        <SimpleTooltip content={t('queueProfiles.form.fields.tierRetryWait.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="tierRetryWait" className="text-left">
            {t('queueProfiles.form.fields.tierRetryWait.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="tierRetryWait"
            type="number"
            min="0"
            value={formData.tierRetryWait}
            onChange={(event) => onFieldChange('tierRetryWait', event.target.value)}
            placeholder={t('queueProfiles.form.fields.tierRetryWait.placeholder')}
          />
        </div>

        {/* Tier Rule Retry Wait */}
        <SimpleTooltip content={t('queueProfiles.form.fields.tierRuleRetryWait.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="tierRuleRetryWait" className="text-left">
            {t('queueProfiles.form.fields.tierRuleRetryWait.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="tierRuleRetryWait"
            type="number"
            min="0"
            value={formData.tierRuleRetryWait}
            onChange={(event) => onFieldChange('tierRuleRetryWait', event.target.value)}
            placeholder={t('queueProfiles.form.fields.tierRuleRetryWait.placeholder')}
          />
        </div>

        {/* Tier Agent Retry Wait */}
        <SimpleTooltip content={t('queueProfiles.form.fields.tierAgentRetryWait.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="tierAgentRetryWait" className="text-left">
            {t('queueProfiles.form.fields.tierAgentRetryWait.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="tierAgentRetryWait"
            type="number"
            min="0"
            value={formData.tierAgentRetryWait}
            onChange={(event) => onFieldChange('tierAgentRetryWait', event.target.value)}
            placeholder={t('queueProfiles.form.fields.tierAgentRetryWait.placeholder')}
          />
        </div>
      </div>
    </FormBox>
  );
}

